import './anfragen.css';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { t, tx } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { NoteRow } from '../belege/Bell';
import { REQUEST_NOTES } from '../belege/labels';
import { useNotifications } from '../belege/shared';
import { Sheet } from '../belege/Sheet';
import { useCloseSheet } from '../lohn/Pages';
import { FIRM_ARTS, requestState } from './labels';
import { RequestForm } from './RequestForm';
import type { RequestItem, RequestsResponse } from './types';

// Teile der Übersicht, die zu „Unterlagen anfordern“ gehören (wie in der Demo: Karte „Sonstige Unterlagen“).

const TONE = { ok: ' ok', warn: ' bad', none: '' } as const;
// Rückfragen oben, dann offene, dann erledigte (jeweils die neuesten zuerst)
const rank = (r: RequestItem) => (r.ask ? 0 : r.status === 'offen' ? 1 : 2);

// Meldungen der Kanzlei zu Anfragen (Datei da, Rückfrage, erledigt), bis sie geöffnet sind (bei mehreren Firmen mit
// Firmennamen)
export function AnfragenNotices() {
  const notes = useNotifications();
  const items = (notes.data?.items ?? []).filter((n) => REQUEST_NOTES.has(n.type) && !n.read);
  if (!items.length) return null;
  return (
    <div className="notice" role="status">
      {items.map((n) => (
        <NoteRow key={n.id} item={n} />
      ))}
    </div>
  );
}

// Karte „Sonstige Unterlagen“ für die oben gewählte Firma: Anfragen mit Stand und „Unterlagen anfordern“.
// Anfragen zu Mitarbeitern stehen mit Namen dabei.
export function AnfragenCard({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const res = useApi<RequestsResponse>(`/api/app/anfragen/${clientId}`, { live: ['anfragen', 'notifications'], clientId });
  const [all, setAll] = useState(false);
  const open = (search: string) => navigate({ search }, { state: { sheet: true } });
  const list = [...(res.data?.requests ?? [])].sort((a, b) => rank(a) - rank(b));
  const offen = list.filter((r) => r.status === 'offen').length;
  const shown = all ? list : list.slice(0, 3);

  return (
    <div className="card rqcard">
      <h3>
        {t('Sonstige Unterlagen')} {offen > 0 && <span>{t('{n} in Bearbeitung', { n: offen })}</span>}
      </h3>
      {shown.length > 0 && (
        <div className="lines">
          {shown.map((r) => {
            const s = requestState(r);
            return (
              <button key={r.id} type="button" className="ln lnb" onClick={() => open(`?anfrage=${r.id}`)}>
                <span>
                  {tx(r.label)}
                  {r.employee && ` · ${r.employee.name}`}
                  <small>
                    {r.note ? `${r.note} · ` : ''}
                    {t('angefordert am {date}', { date: fmtDate(r.createdAt) })}
                  </small>
                </span>
                <span className={`st${TONE[s.tone]}`}>{r.fresh && !r.ask ? `${t('Neu')} · ${t(s.label)}` : t(s.label)} ›</span>
              </button>
            );
          })}
        </div>
      )}
      {list.length > 3 && (
        <button type="button" className="more" aria-expanded={all} onClick={() => setAll((x) => !x)}>
          {all ? t('Weniger anzeigen') : t('Alle anzeigen ({n})', { n: list.length })}
          <span aria-hidden="true">{all ? '▴' : '▾'}</span>
        </button>
      )}
      <div className="bwa-row">
        <button type="button" className="btn2 dark" onClick={() => open('?anfordern=1')}>
          {t('Unterlagen anfordern')}
        </button>
      </div>
      <div className="src">
        {t(
          'Zum Beispiel SuSa (Summen- und Saldenliste, Ihre Kontenübersicht) oder Unbedenklichkeitsbescheinigung. Ihre Kanzlei lädt sie Ihnen hoch. Bescheinigungen zu einem Mitarbeiter fordern Sie in seiner Personalakte an.',
        )}
      </div>
    </div>
  );
}

// „Unterlagen anfordern“ als Fenster (?anfordern=1) für die oben gewählte Firma
export function RequestFormSheetHost({ clientId, clientName }: { clientId: string; clientName: string }) {
  const [params] = useSearchParams();
  const close = useCloseSheet('anfordern');
  if (!params.get('anfordern')) return null;
  return (
    <Sheet title={t('Unterlagen anfordern')} onClose={close}>
      <p className="meta">{clientName}</p>
      <RequestForm
        arts={FIRM_ARTS}
        url={`/api/app/anfragen/${clientId}`}
        placeholder={t('z. B. Stand 30.09.2026, für die Bank')}
        hint={t('Die Anfrage geht an Ihre Kanzlei. Den Stand sehen Sie auf der Übersicht unter „Sonstige Unterlagen“.')}
        onDone={close}
        onCancel={close}
      />
    </Sheet>
  );
}
